import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/* The base holds only what is true of every button in every theme: layout, focus, disabled.
   Appearance lives in the variants, so the external theme changes buttons through its role
   tokens instead of forking the component. A disabled button still matches `:hover`, so each
   variant guards its hover with `not-disabled:` — never `enabled:`, which an `asChild` button
   rendered as a link would never match. */
const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-control text-(length:--control-font-size) leading-5 font-(--control-font-weight) transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-ring/45 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger aria-invalid:ring-danger/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-content-on-accent not-disabled:hover:bg-accent-hover active:bg-accent-pressed focus-visible:border-accent-pressed',
        /* Same role as `primary`mainly used in an error page, an onboarding
           step — where a single decision carries the view. Never next to a `primary`. */
        strong:
          'bg-accent-strong text-content-on-accent not-disabled:hover:bg-accent-strong-hover active:bg-accent-strong',
        /* The action that matters on the screen without being the screen's own. Teal also marks
           what is selected, but that lives on controls — a checkbox, a tab, a toggle. */
        secondary:
          'bg-accent-secondary text-content-on-accent not-disabled:hover:bg-accent-secondary-hover active:bg-accent-secondary-hover',
        'secondary-outline':
          'border border-accent-secondary bg-surface text-accent-secondary not-disabled:hover:border-accent-secondary-hover not-disabled:hover:bg-accent-secondary/10 not-disabled:hover:text-accent-secondary-hover',
        tertiary:
          'bg-surface-emphasis text-content not-disabled:hover:bg-surface-emphasis/70 active:bg-surface-emphasis/90',
        outline:
          'border border-control-border bg-surface text-content transition-shadow not-disabled:hover:border-control-border-hover not-disabled:hover:shadow-(--control-shadow-hover)',
        ghost:
          'text-content-secondary not-disabled:hover:bg-surface-muted not-disabled:hover:text-content',
        destructive:
          'bg-danger-surface text-danger not-disabled:hover:bg-danger not-disabled:hover:text-content-on-accent active:bg-danger-hover',
        /* A bottom border, not `underline`: the button is inline-flex, so text-decoration
           stops at the label and leaves the icon above a gap in the line. */
        link: 'h-auto! rounded-none px-0! gap-1.5 border-b border-current pb-0.5 text-content not-disabled:hover:text-accent',
        unstyled: '',
      },
      size: {
        default:
          'h-(--control-height) px-(--control-padding-x) py-2 has-[>svg]:px-3',
        sm: 'h-7 gap-1.5 px-3 text-xs! has-[>svg]:px-2',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-7 p-1.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      // Without this, every button in a form submits it.
      type={asChild ? type : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
